<template>
  <div>
    <vx-card class="mb-4">
      <h4 class="mb-1">{{ $t('influencer_level.title') }}</h4>
      <small class="text-muted d-block mb-3">{{ $t('influencer_level.hint') }}</small>

      <div class="vx-row">
        <div class="vx-col sm:w-5/12 w-full">
          <b-form-group :label="$t('influencer_level.name')" label-for="name">
            <b-form-input id="name" v-model="model.name" type="text" class="required"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-3/12 w-full">
          <b-form-group :label="$t('influencer_level.rank')" label-for="rank">
            <b-form-input id="rank" v-model="model.rank" type="number" min="1" class="required"/>
            <small class="text-muted">{{ $t('influencer_level.rank_hint') }}</small>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('influencer_level.bonus')" label-for="commission_bonus_pct">
            <b-input-group append="%">
              <b-form-input id="commission_bonus_pct" v-model="model.commission_bonus_pct" type="number" step="0.1" min="0" max="100"/>
            </b-input-group>
            <small class="text-muted">{{ $t('influencer_level.bonus_hint') }}</small>
          </b-form-group>
        </div>
      </div>

      <h5 class="mb-1">{{ $t('influencer_level.requirements') }}</h5>
      <small class="text-muted d-block mb-2">{{ $t('influencer_level.requirements_hint') }}</small>

      <div class="vx-row">
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('influencer_level.min_followers')" label-for="min_followers">
            <b-form-input id="min_followers" v-model="model.min_followers" type="number" min="0"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('influencer_level.min_conversions')" label-for="min_conversions">
            <b-form-input id="min_conversions" v-model="model.min_conversions" type="number" min="0"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('influencer_level.min_sales_amount')" label-for="min_sales_amount">
            <b-input-group prepend="R$">
              <b-form-input id="min_sales_amount" v-model="model.min_sales_amount" type="number" step="0.01" min="0"/>
            </b-input-group>
          </b-form-group>
        </div>
      </div>

      <div class="vx-row">
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('influencer_level.badge_color')" label-for="badge_color">
            <b-form-select id="badge_color" v-model="model.badge_color" :options="colorList"/>
          </b-form-group>
        </div>
        <div class="vx-col sm:w-4/12 w-full">
          <b-form-group :label="$t('influencer_level.status')" label-for="status">
            <b-form-radio-group id="status" v-model="model.status" :options="statusList"/>
          </b-form-group>
        </div>
      </div>

      <b-form-group :label="$t('influencer_level.benefits')" label-for="benefits">
        <b-form-textarea id="benefits" v-model="model.benefits" rows="2"/>
      </b-form-group>

      <b-form-row class="justify-end mt-2">
        <b-col cols="auto">
          <b-button variant="primary" @click="createOrUpdate">{{ $t('save') }}</b-button>
        </b-col>
        <b-col cols="auto">
          <b-button variant="outline-secondary" @click="goBack">
            <feather-icon icon="CornerUpLeftIcon" class="mr-50"/>
            <span class="align-middle">{{ $t('back') }}</span>
          </b-button>
        </b-col>
      </b-form-row>
    </vx-card>
  </div>
</template>

<script>
import {
  BFormGroup, BFormInput, BFormTextarea, BFormSelect, BFormRadioGroup,
  BButton, BFormRow, BCol, BInputGroup
} from 'bootstrap-vue'
import InfluencerLevelService from '@/services/api/InfluencerLevelService'

export default {
  components: {
    BFormGroup, BFormInput, BFormTextarea, BFormSelect, BFormRadioGroup,
    BButton, BFormRow, BCol, BInputGroup
  },
  props: {
    id: { type: [Number, String], default: null }
  },
  data: () => ({
    service: null,
    model: {
      id: null,
      name: '',
      rank: 1,
      commission_bonus_pct: 0,
      min_followers: null,
      min_conversions: null,
      min_sales_amount: null,
      badge_color: 'primary',
      benefits: '',
      status: 'AC'
    },
    statusList: [
      { text: 'Ativo', value: 'AC' },
      { text: 'Inativo', value: 'IN' }
    ],
    colorList: [
      { text: 'Azul', value: 'primary' },
      { text: 'Verde', value: 'success' },
      { text: 'Amarelo', value: 'warning' },
      { text: 'Vermelho', value: 'danger' },
      { text: 'Cinza', value: 'dark' }
    ]
  }),
  methods: {
    loadData(id) {
      this.$vs.loading()
      this.service.read(id).then(
        response => { Object.keys(this.model).forEach(k => { this.model[k] = response[k] }) },
        () => { this.$vs.loading.close() }
      ).finally(() => { this.$vs.loading.close() })
    },
    createOrUpdate() {
      this.$vs.loading()
      // Campo vazio precisa virar null: '' seria castado para 0, e "sem exigência
      // de seguidores" viraria "exige 0 seguidores" — que não é a mesma coisa na
      // hora de escolher o nível mais alto que a pessoa atende.
      const blank = v => (v === '' || v === undefined ? null : v)

      this.service.createOrUpdate({
        id: this.id,
        name: this.model.name,
        rank: this.model.rank,
        commission_bonus_pct: this.model.commission_bonus_pct || 0,
        min_followers: blank(this.model.min_followers),
        min_conversions: blank(this.model.min_conversions),
        min_sales_amount: blank(this.model.min_sales_amount),
        badge_color: this.model.badge_color,
        benefits: this.model.benefits,
        status: this.model.status
      }).then(
        data => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          if (_.isNil(this.id)) this.$router.push(`/influencer_levels/${data.id}/edit`)
        },
        error => this.showErrors(this.$validator, error, this)
      ).finally(() => { this.$vs.loading.close() })
    },
    goBack() {
      this.$router.push('/influencer_levels')
    }
  },
  beforeMount() {
    this.service = InfluencerLevelService.build(this.$vs)
  },
  mounted() {
    if (this.id) this.loadData(this.id)
  }
}
</script>
